import React from 'react';
import { Pressable, View } from 'react-native';
import type { Category } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useCatLabel, useItemsLabel } from '../i18n';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { CountBadge } from './CountBadge';
import { T } from './Text';

/**
 * Step 2 of the order flow shows category names only — no prices, no items —
 * so the counter picks a section first and the screen stays readable.
 */
export function CategoryCard({
  cat,
  itemCount,
  offerCount,
  onPress,
}: {
  cat: Category;
  itemCount: number;
  offerCount: number;
  onPress: () => void;
}) {
  const colors = useColors();
  const items = useItemsLabel();
  const catLabel = useCatLabel();

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }: any) => ({
        flexGrow: 1,
        flexBasis: 190,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 14,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : colors.line,
        backgroundColor: colors.surface,
        opacity: pressed ? 0.85 : 1,
      })}
    >
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: radius.md,
          backgroundColor: colors.surface2,
          borderWidth: 1,
          borderColor: colors.line,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={cat.icon as IconName} size={20} color={colors.gold} />
      </View>

      <View style={{ flex: 1, gap: 3 }}>
        <T size={14.5} weight={700} numberOfLines={1}>
          {catLabel(cat.id)}
        </T>
        <T size={11.5} color={colors.textFaint}>
          {items(itemCount)}
        </T>
      </View>

      {offerCount > 0 ? <CountBadge value={`${offerCount}`} tone="spice" /> : null}
      <Icon name="chevronRight" size={16} color={colors.textFaint} />
    </Pressable>
  );
}
